import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import * as MessageManager from 'notistack';
import { ToastProvider } from '../ToastProvider/ToastProvider';
import { AlertGroupName } from '../../hooks/useAlertState/useAlertState';
import { useResponsive } from '../../hooks/useResponsive/useResponsive';
import { BaseDiv } from '../../common/BaseElements';
import Fade from '../../vendor/toast-fade/ToastFadeTransition';

export interface IToastMessageProviderProps {
  groupName?: AlertGroupName;
}

/* *
 * Exporting `notistack` as ToastMessageManager to be used later in the apps.
 * `notistack` should not be used directly anywhere in the app, this should act as a single access point to use it.
 * This prevents using mutiple versions of notistack later in apps.
 * */
export const ToastMessageManager = MessageManager;

const Content = styled(BaseDiv)``;

const ANCHOR_PROPS_MOBILE = `mobile`;
const ANCHOR_PROPS_DESKTOP = `desktop`;

/* *
 * Returns notification anchor props based on the screen size.
 * */
const anchorPropsByScreen = {
  [ANCHOR_PROPS_MOBILE]: { horizontal: `center`, vertical: `bottom` },
  [ANCHOR_PROPS_DESKTOP]: { horizontal: `left`, vertical: `bottom` }
};

export const AUTO_HIDE_DURATION = 5000;
export const FADE_DURATION = 250;

/* *
 * Global app message provider.
 * Using notistack to manage application notifications.
 * Should act as a wrapper for apps. App contents need to be passed as children.
 * Using global `ProviderCss` styles to attach required styles to notistack containers.
 * Manages responsive container behavior by using useResponsive hook.
 *
 * Usage:
 * <ToastMessageProvider>
 *   <Content />
 * </ToastMessageProvider>
 *
 * Implemented with ToastProvider to listen for toast state changes using useAlertState.
 * */
export const ToastMessageProvider: React.FC<PropsWithChildren<IToastMessageProviderProps>> = ({ groupName = AlertGroupName.Toast, children }) => {
  const { isMobile, ref } = useResponsive({
    tolerance: 1,
    debounceInMs: 1
  });
  const anchor = anchorPropsByScreen[isMobile ? ANCHOR_PROPS_MOBILE : ANCHOR_PROPS_DESKTOP] as MessageManager.SnackbarOrigin;

  return (
    <>
      <ToastMessageManager.SnackbarProvider
        TransitionComponent={Fade as any}
        transitionDuration={FADE_DURATION}
        anchorOrigin={anchor}
        autoHideDuration={AUTO_HIDE_DURATION}
      >
        <ToastProvider groupName={groupName}>
          <Content ref={ref}>{children}</Content>
        </ToastProvider>
      </ToastMessageManager.SnackbarProvider>
    </>
  );
};
